import React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Box } from '@mui/material';
import FinmapleComparator from '../components/Comparator/FinmapleComparator';
import NewsletterSection from '../components/Layout/NewsletterSection';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';
import AtomicAnswer from '../components/SEO/AtomicAnswer';
import FAQSection from '../components/SEO/FAQSection';
import SectionDivider from '../components/Home/SectionDivider';
import EditorialReview from '../components/Layout/EditorialReview';
import CrossLinks from '../components/Layout/CrossLinks';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FinancialService',
  name: 'Comparador de Remesas desde Canadá - FinMaple',
  description: 'Compara las mejores opciones para enviar dinero desde Canadá a Latinoamérica: Wise, Remitly, Simplii, RBC, Western Union.',
  provider: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
  dateModified: '2026-02-01',
};

const faqs = [
  {
    question: '¿Cuál es la forma más barata de enviar dinero a Colombia desde Canadá?',
    answer: 'Wise (TransferWise) usa la tasa mid-market real sin markup, con fees de 0.41-0.62%. Para clientes Simplii, las transferencias internacionales son gratis. Western Union es la opción más cara pero ofrece cash pickup en minutos.',
  },
  {
    question: '¿Cuánto tiempo tarda en llegar el dinero?',
    answer: 'Wise: 1-3 días hábiles. Remitly Express: minutos. Simplii/RBC: 1-5 días. Western Union cash pickup: minutos. La velocidad depende del método de envío y recepción.',
  },
  {
    question: '¿Puedo enviar dinero a Latinoamérica gratis?',
    answer: 'Sí, si tienes cuenta en Simplii Financial o RBC. Ambos ofrecen transferencias internacionales sin fee para sus clientes. Sin embargo, el exchange rate puede tener un markup pequeño comparado con Wise.',
  },
  {
    question: '¿Cuánto dinero puedo enviar al exterior?',
    answer: 'Depende del proveedor: Wise permite hasta $1.5M CAD con cuenta verificada. Western Union tiene límite de $7,999 por transferencia. Los bancos varían. Para montos grandes, siempre verifica los límites y reporta transferencias mayores a $10,000 CAD según la ley.',
  },
];

const ComparadorRemesasPage = () => {
  const { i18n } = useTranslation();
  return (
  <Box sx={{ bgcolor: '#FFFFFF' }}>
    <SEOHead
      seoKey="remittanceComparator"
      title="Mejores Opciones para Enviar Remesas desde Canadá 2026"
      description="Compara fees, tasas de cambio y velocidad de Wise, Remitly, Simplii, RBC y Western Union. Envía dinero a Colombia, México y más."
      canonical="/comparar-remesas"
      routeKey="remittanceComparator"
      schema={schema}
      noIndex={i18n.language !== 'es'}
    />
    <PageHeader
      variant="functional"
      title="Las mejores opciones para enviar remesas desde Canadá - comparadas para 2026"
      subtitle="Compara fees, tasas de cambio y velocidad de 5 proveedores. CAD a COP, MXN, PEN y más."
      breadcrumbs={[{ label: 'Remesas' }]}
      maxWidth="lg"
    />
    <SectionDivider from="#F7F9FC" to="#FFFFFF" />
    <EditorialReview />
    <Container maxWidth="lg" sx={{ mb: 2 }}>
      <AtomicAnswer text="Para enviar dinero a Latinoamérica, Wise ofrece la mejor tasa de cambio (mid-market real). Remitly es el más rápido (minutos con Express). Simplii y RBC son gratis si ya tienes cuenta. Western Union solo para cash pickup urgente - es el más caro." />
    </Container>
    <Container maxWidth="lg">
      <FinmapleComparator category="remittances" />
    </Container>
    <Container maxWidth="lg">
      <CrossLinks
        links={[
          { label: 'Comparar cuentas bancarias', to: '/comparar-cuentas' },
          { label: 'Tarjetas de crédito', to: '/tarjetas-de-credito' },
          { label: 'Seguros para newcomers', to: '/comparar-seguros' },
        ]}
      />
    </Container>
    <Box sx={{ bgcolor: '#F7F9FC', mt: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <FAQSection faqs={faqs} />
      </Container>
    </Box>
    <NewsletterSection />
  </Box>
  );
};

export default ComparadorRemesasPage;
